import React, { useState, useEffect } from "react";
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, isSameMonth, isSameDay, addDays, parseISO } from "date-fns";
import AddIcon from "@mui/icons-material/Add";
import CircleIcon from "@mui/icons-material/Circle";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import Dialog from "@mui/material/Dialog";
import toast, { Toaster } from "react-hot-toast";
import { gapi } from "gapi-script";
import { useEventStore } from "../../../stores/eventStore";
import { useDepartment } from "../../../stores/departmentStore";

const CalendarModal = ({ isOpen, onClose, events: googleEvents, refreshGoogleEvents, currentDate, setCurrentDate, selectedDate, setSelectedDate, isConnected, userProfile, handleLogout }) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [editingEventId, setEditingEventId] = useState(null);
  const { localEvents, fetchLocalEvents, createLocalEvent, updateLocalEvent } = useEventStore();
  const { departments, listDepartments } = useDepartment();

  const [title, setTitle] = useState("");
  const [startTime, setStartTime] = useState("09:00");
  const [endTime, setEndTime] = useState("10:00");
  const [category, setCategory] = useState("Work");
  const [departmentId, setDepartmentId] = useState("");
  const [notes, setNotes] = useState("");
  const [eventDate, setEventDate] = useState(format(selectedDate, "yyyy-MM-dd"));
  const [selectedCategories, setSelectedCategories] = useState(["Work", "Personal", "Team", "Holidays", "Google"]);
  const [syncToGoogle, setSyncToGoogle] = useState(isConnected);

  useEffect(() => {
    setSyncToGoogle(isConnected);
  }, [isConnected]);

  useEffect(() => {
    setEventDate(format(selectedDate, "yyyy-MM-dd"));
  }, [selectedDate, isFormOpen]);

  useEffect(() => {
    if (isOpen) {
      fetchLocalEvents();
      listDepartments();
    }
  }, [isOpen, fetchLocalEvents, listDepartments]);

  const resetForm = () => {
    setIsFormOpen(false);
    setEditingEventId(null);
    setTitle("");
    setNotes("");
    setStartTime("09:00");
    setEndTime("10:00");
  };

  const handleSubmitEvent = async (e) => {
    e.preventDefault();
    
    const [startH, startM] = startTime.split(":");
    const startD = new Date(eventDate);
    startD.setHours(parseInt(startH, 10));
    startD.setMinutes(parseInt(startM, 10));
    
    const [endH, endM] = endTime.split(":");
    const endD = new Date(eventDate);
    endD.setHours(parseInt(endH, 10));
    endD.setMinutes(parseInt(endM, 10));

    const deptName = departments.find(d => d.id === departmentId)?.name || "";

    const eventPayload = {
      title,
      start_date: startD.toISOString(),
      end_date: endD.toISOString(),
      category,
      department_id: departmentId,
      department_name: deptName,
      notes
    };

    if (editingEventId) {
      const success = await updateLocalEvent(editingEventId, eventPayload);
      if (success) {
        toast.success("Event updated successfully!");
        resetForm();
      } else {
        toast.error("Failed to update event.");
      }
    } else {
      const success = await createLocalEvent(eventPayload);

      if (success) {
        if (syncToGoogle && gapi?.client) {
          try {
            await gapi.client.load('calendar', 'v3');
            await gapi.client.calendar.events.insert({
              calendarId: 'primary',
              resource: {
                summary: title,
                description: notes,
                start: {
                  dateTime: startD.toISOString(),
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                },
                end: {
                  dateTime: endD.toISOString(),
                  timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }
              }
            });
            toast.success("Event added to TwoKey and Google Calendar!");
          } catch (err) {
            console.error("Error creating Google event", err);
            toast.error("Created locally but failed to sync to Google Calendar.");
          }
        } else {
          toast.success("Event added successfully!");
        }
        resetForm();
      } else {
        toast.error("Failed to create event. Please try again.");
      }
    }
  };

  const handleEditEvent = (e) => {
    if (e.isGoogle) return; // Google events read-only for now

    setEditingEventId(e.id);
    setTitle(e.title);
    setEventDate(format(e.date, "yyyy-MM-dd"));
    setStartTime(format(e.date, "HH:mm"));
    setEndTime(format(e.end_date, "HH:mm"));
    setCategory(e.category);
    setDepartmentId(e.department_id || "");
    setNotes(e.notes || "");
    setIsFormOpen(true);
  };

  const handleDeleteEvent = async (e, eventId) => {
    e.stopPropagation();
    if (window.confirm("Are you sure you want to delete this event?")) {
      const { deleteLocalEvent } = useEventStore.getState();
      await deleteLocalEvent(eventId);
    }
  };

  const events = [...googleEvents, ...localEvents];
  
  const filteredEvents = events.filter(e => {
    if (e.isGoogle) return selectedCategories.includes("Google");
    return selectedCategories.includes(e.category);
  }).sort((a, b) => new Date(a.date) - new Date(b.date));

  if (!isOpen) return null;

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const jumpToToday = () => {
    const today = new Date();
    setCurrentDate(today);
    setSelectedDate(today);
  };

  const onDateClick = (day) => {
    setSelectedDate(day);
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      if (refreshGoogleEvents && isConnected) {
        await refreshGoogleEvents();
      }
      await fetchLocalEvents();
      toast.success("Calendar synced successfully");
    } catch (err) {
      toast.error("Failed to sync calendar");
    } finally {
      setIsRefreshing(false);
    }
  };

  // Render header
  const renderHeader = () => {
    return (
      <div className="flex justify-between items-center px-4 py-2 bg-white sticky top-0 z-10 border-b">
        <div className="flex items-center gap-2">
          <span className="text-gray-600 font-semibold text-sm ml-2">TwoKey Calendar — {format(currentDate, "MMMM yyyy")}</span>
        </div>
        <div className="flex items-center gap-3">
          {isConnected && userProfile && (
            <div className="flex items-center gap-2 border-r pr-3 mr-1">
              <img src={userProfile.imageUrl} alt="Profile" className="w-6 h-6 rounded-full border border-gray-200" />
              <span className="text-xs text-gray-600 font-medium">{userProfile.email}</span>
              <button onClick={handleLogout} className="text-xs text-red-500 hover:text-red-700 font-bold ml-1">Logout</button>
            </div>
          )}
          <button 
            onClick={handleRefresh} 
            disabled={isRefreshing}
            className={`p-1 hover:bg-gray-100 rounded-full ${isRefreshing ? 'opacity-50' : ''}`}
            title="Refresh Calendar"
          >
            <RefreshIcon fontSize="small" className={`text-gray-500 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-full">
            <CloseIcon fontSize="small" className="text-gray-500" />
          </button>
        </div>
      </div>
    );
  };

  // Render days of week
  const renderDays = () => {
    const days = [];
    const startDate = startOfWeek(currentDate, { weekStartsOn: 1 }); // Monday start
    for (let i = 0; i < 7; i++) {
      days.push(
        <div className="text-center font-semibold text-xs text-gray-500 uppercase py-2" key={i}>
          {format(addDays(startDate, i), "EE")}
        </div>
      );
    }
    return <div className="grid grid-cols-7 bg-white border-b">{days}</div>;
  };

  // Render cells
  const renderCells = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });

    const rows = [];
    let days = [];
    let day = startDate;
    let formattedDate = "";

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        formattedDate = format(day, "d");
        const cloneDay = day;
        
        // Find events for this day
        const dayEvents = filteredEvents.filter(e => isSameDay(e.date, cloneDay));

        days.push(
          <div
            className={`min-w-0 min-h-[100px] border-r border-b p-1 flex flex-col gap-1 transition-colors
              ${!isSameMonth(day, monthStart) ? "text-gray-300 bg-gray-50/50" : "text-gray-800 bg-white"}
              ${isSameDay(day, selectedDate) ? "bg-indigo-50" : "hover:bg-gray-50"}
              cursor-pointer`}
            key={day}
            onClick={() => onDateClick(cloneDay)}
          >
            <div className="flex justify-between items-start">
               <span className={`text-sm font-semibold w-7 h-7 flex items-center justify-center rounded-lg ${isSameDay(day, new Date()) ? "bg-indigo-600 text-white" : ""}`}>
                  {formattedDate}
               </span>
               {isSameDay(day, new Date()) && <span className="text-[10px] font-bold text-indigo-600 bg-indigo-100 px-1 rounded">TODAY</span>}
            </div>
            <div className="flex flex-col gap-1 overflow-y-auto max-h-[80px] scrollbar-thin pb-1">
              {dayEvents.map((e, idx) => {
                let colorClass = "bg-gray-100 text-gray-700";
                if (e.isGoogle) colorClass = "bg-blue-100 text-blue-700";
                else if (e.category === "Work") colorClass = "bg-purple-100 text-purple-700";
                else if (e.category === "Personal") colorClass = "bg-green-100 text-green-700";
                else if (e.category === "Team") colorClass = "bg-indigo-100 text-indigo-700";
                else if (e.category === "Holidays") colorClass = "bg-red-100 text-red-700";

                return (
                  <div key={idx} className={`shrink-0 text-[10px] font-semibold truncate px-1.5 py-0.5 rounded ${colorClass}`} title={e.title}>
                    {format(e.date, "h:mm a")} {e.title}
                  </div>
                );
              })}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div className="grid grid-cols-7 flex-1" key={day}>
          {days}
        </div>
      );
      days = [];
    }
    return <div className="flex-1 flex flex-col">{rows}</div>;
  };

  const selectedDayEvents = filteredEvents.filter(e => isSameDay(e.date, selectedDate));

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose} 
      maxWidth="lg" 
      fullWidth 
      PaperProps={{
        style: {
          borderRadius: '16px',
          height: '85vh',
          maxHeight: '90vh',
          overflow: 'hidden',
          backgroundColor: '#ffffff'
        }
      }}
    >
      <div className="w-full h-full flex flex-col overflow-hidden relative">
        {renderHeader()}
        
        <div className="flex flex-1 overflow-hidden">
          {/* Left Sidebar */}
          <div className="w-64 bg-gray-50 border-r p-4 flex flex-col gap-6 overflow-y-auto">
            <div className="flex flex-col gap-3">
              <button onClick={() => setIsFormOpen(true)} className="w-full bg-[#4A3AFF] hover:bg-indigo-700 text-white rounded-lg py-2.5 font-medium flex items-center justify-center gap-2 transition-colors">
                <AddIcon fontSize="small" /> New Event
              </button>
              <button onClick={jumpToToday} className="w-full bg-white border border-indigo-200 text-indigo-600 rounded-lg py-2 font-medium hover:bg-indigo-50 transition-colors">
                Jump to Today
              </button>
            </div>
            
            <div>
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">My Calendars</h4>
              <div className="flex flex-col gap-2">
                {[
                  { name: "Google", color: "text-blue-600", count: events.filter(e => e.isGoogle).length },
                  { name: "Work", color: "text-purple-500", count: events.filter(e => !e.isGoogle && e.category === "Work").length },
                  { name: "Personal", color: "text-green-500", count: events.filter(e => !e.isGoogle && e.category === "Personal").length },
                  { name: "Team", color: "text-blue-500", count: events.filter(e => !e.isGoogle && e.category === "Team").length },
                  { name: "Holidays", color: "text-red-500", count: events.filter(e => !e.isGoogle && e.category === "Holidays").length }
                ].map((cal) => (
                  <label key={cal.name} className="flex items-center justify-between cursor-pointer group">
                    <div className="flex items-center gap-2">
                      <input 
                        type="checkbox" 
                        checked={selectedCategories.includes(cal.name)} 
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedCategories(prev => [...prev, cal.name]);
                          } else {
                            setSelectedCategories(prev => prev.filter(c => c !== cal.name));
                          }
                        }}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 border-gray-300" 
                      />
                      <CircleIcon className={`${cal.color} w-3 h-3`} style={{ fontSize: '12px' }} />
                      <span className="text-sm font-medium text-gray-700 group-hover:text-gray-900">{cal.name}</span>
                    </div>
                    <span className="text-xs font-medium text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">{cal.count}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="mt-auto">
              <h4 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">Selected Day</h4>
              <div className="bg-white border rounded-xl p-3 shadow-sm">
                <p className="text-sm font-bold text-gray-800">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
                <p className="text-xs text-gray-500 mt-1">{selectedDayEvents.length} events</p>
              </div>
            </div>
          </div>

          {/* Center Calendar Grid */}
          <div className="flex-1 flex flex-col bg-white overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-1 border rounded-lg bg-white p-0.5 shadow-sm">
                  <button onClick={prevMonth} className="p-1 hover:bg-gray-100 rounded-md text-gray-600"><ChevronLeftIcon fontSize="small" /></button>
                  <button onClick={nextMonth} className="p-1 hover:bg-gray-100 rounded-md text-gray-600"><ChevronRightIcon fontSize="small" /></button>
                </div>
                <h2 className="text-lg font-bold text-gray-800">{format(currentDate, "MMMM yyyy")}</h2>
              </div>
              <div className="flex items-center gap-4 text-xs font-medium text-gray-500">
                 <span className="flex items-center gap-1"><CircleIcon className="text-purple-500" style={{ fontSize: '10px' }} /> Work</span>
                 <span className="flex items-center gap-1"><CircleIcon className="text-green-500" style={{ fontSize: '10px' }} /> Personal</span>
                 <span className="flex items-center gap-1"><CircleIcon className="text-blue-500" style={{ fontSize: '10px' }} /> Team</span>
                 <span className="flex items-center gap-1"><CircleIcon className="text-red-500" style={{ fontSize: '10px' }} /> Holidays</span>
              </div>
            </div>
            {renderDays()}
            <div className="flex-1 overflow-y-auto scrollbar-thin flex flex-col bg-gray-50">
              {renderCells()}
            </div>
          </div>

          {/* Events Section (Right Side) */}
          <div className="w-80 bg-gray-50 border-l flex flex-col h-full overflow-hidden">
            {isFormOpen ? (
              <form onSubmit={handleSubmitEvent} className="flex flex-col h-full bg-gray-50">
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                  <h3 className="font-bold text-gray-800">{editingEventId ? "Edit Event" : "New Event"}</h3>
                  <button type="button" onClick={() => { resetForm(); setIsFormOpen(false); }} className="text-gray-400 hover:text-gray-600"><CloseIcon fontSize="small" /></button>
                </div>
                <div className="flex-1 p-4 flex flex-col gap-4 overflow-y-auto scrollbar-thin min-h-0">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">Title</label>
                    <input 
                      required
                      type="text" 
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full border border-indigo-200 bg-gray-50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#4A3AFF] outline-none"
                      placeholder="Add title"
                    />
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">Date</label>
                    <input 
                      type="date" 
                      value={eventDate}
                      onChange={(e) => setEventDate(e.target.value)}
                      required
                      className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#4A3AFF] outline-none"
                    />
                  </div>

                  <div className="flex gap-3">
                     <div className="flex-1">
                      <label className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">Start</label>
                      <input 
                        required
                        type="time" 
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-[#4A3AFF] outline-none"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">End</label>
                      <input 
                        required
                        type="time" 
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-[#4A3AFF] outline-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-[10px] font-bold text-gray-400 mb-2 tracking-wider uppercase">Calendar</label>
                    <div className="flex flex-wrap gap-2">
                      {["Work", "Personal", "Team", "Holidays"].map((cat) => {
                        let dotColor = "text-gray-500";
                        if (cat === "Work") dotColor = "text-purple-500";
                        if (cat === "Personal") dotColor = "text-green-500";
                        if (cat === "Team") dotColor = "text-blue-500";
                        if (cat === "Holidays") dotColor = "text-red-500";

                        const isSelected = category === cat;

                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => setCategory(cat)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border ${isSelected ? 'border-indigo-600 bg-white text-indigo-700 shadow-sm' : 'border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100'}`}
                          >
                            <CircleIcon className={dotColor} style={{ fontSize: '10px' }} />
                            {cat}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {["Team", "Work", "Holidays"].includes(category) && (
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">Share with Department</label>
                      <select 
                        required
                        value={departmentId}
                        onChange={(e) => setDepartmentId(e.target.value)}
                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#4A3AFF] outline-none"
                      >
                        <option value="" disabled>Select department</option>
                        <option value="All">All Departments</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.name}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  <div className="flex-1">
                    <label className="block text-[10px] font-bold text-gray-400 mb-1 tracking-wider uppercase">Notes</label>
                    <textarea 
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="w-full h-24 border border-gray-200 bg-gray-50 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-[#4A3AFF] outline-none resize-none"
                      placeholder="Add notes..."
                    />
                  </div>

                  {isConnected && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input 
                        type="checkbox" 
                        checked={syncToGoogle}
                        onChange={(e) => setSyncToGoogle(e.target.checked)}
                        className="w-4 h-4 rounded text-[#4A3AFF] focus:ring-[#4A3AFF] border-gray-300" 
                      />
                      <span className="text-xs font-bold text-gray-600">Add to Google Calendar</span>
                    </label>
                  )}

                  <div className="pt-2 flex justify-end gap-2 mt-auto">
                    <button type="button" onClick={resetForm} className="px-4 py-2 text-sm font-bold text-gray-600 hover:bg-gray-200 bg-gray-200/50 rounded-lg transition-colors">Cancel</button>
                    <button type="submit" className="px-4 py-2 text-sm font-bold text-white bg-[#4A3AFF] hover:bg-indigo-700 rounded-lg shadow-sm transition-colors">{editingEventId ? "Update" : "Create"}</button>
                  </div>
                </div>
              </form>
            ) : (
              <>
                <div className="p-4 border-b flex justify-between items-center bg-gray-50">
                  <h3 className="font-bold text-gray-800">{format(selectedDate, "EEEE, MMMM d, yyyy")}</h3>
                  <button onClick={() => { resetForm(); setIsFormOpen(true); }} className="text-gray-400 hover:text-gray-600"><AddIcon fontSize="small" /></button>
                </div>
                <div className="flex-1 p-4 flex flex-col gap-3 overflow-y-auto scrollbar-thin min-h-0">
                  {selectedDayEvents.length === 0 ? (
                    <div className="text-center text-gray-400 text-sm mt-10">No events scheduled.</div>
                  ) : (
                    selectedDayEvents.map((e, idx) => {
                      let barColorClass = "bg-gray-500";
                      let dotColorClass = "text-gray-500";
                      let textClass = "text-gray-600";
                      
                      if (e.isGoogle) { barColorClass = "bg-blue-500"; dotColorClass = "text-blue-500"; textClass = "text-blue-600"; }
                      else if (e.category === "Work") { barColorClass = "bg-purple-500"; dotColorClass = "text-purple-500"; textClass = "text-purple-600"; }
                      else if (e.category === "Personal") { barColorClass = "bg-green-500"; dotColorClass = "text-green-500"; textClass = "text-green-600"; }
                      else if (e.category === "Team") { barColorClass = "bg-indigo-500"; dotColorClass = "text-indigo-500"; textClass = "text-indigo-600"; }
                      else if (e.category === "Holidays") { barColorClass = "bg-red-500"; dotColorClass = "text-red-500"; textClass = "text-red-600"; }

                      return (
                        <div 
                          key={idx} 
                          onClick={() => handleEditEvent(e)}
                          className="shrink-0 bg-white border border-gray-200 rounded-xl p-3 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-1 relative overflow-hidden group cursor-pointer"
                        >
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${barColorClass}`}></div>
                          <div className="flex justify-between items-start">
                            <span className="text-xs font-semibold text-gray-500 pl-2">{format(e.date, "h:mm a")}</span>
                            {!e.isGoogle && (
                              <button 
                                onClick={(ev) => handleDeleteEvent(ev, e.id)}
                                className="text-gray-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                              >
                                <DeleteOutlineIcon style={{ fontSize: '14px' }} />
                              </button>
                            )}
                          </div>
                          <span className="text-sm font-bold text-gray-800 pl-2 break-words whitespace-normal break-all">{e.title}</span>
                          <div className="flex items-center gap-1 mt-1 pl-2">
                             <CircleIcon className={dotColorClass} style={{ fontSize: '10px' }} />
                             <span className={`text-xs font-medium ${textClass}`}>{e.isGoogle ? "Google" : e.category} {e.department_name ? `(${e.department_name})` : ''}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <Toaster position="bottom-left" reverseOrder={false} />
    </Dialog>
  );
};

export default CalendarModal;
